/**
 * Hook for managing request tabs (Drafts, Current, Past)
 * Follows SOLID: Single Responsibility - only handles tab filtering logic
 */
import { useMemo } from 'react';
import { type Request, RequestStatus } from '@features/requests/types';
import { normalizeToStartOfDay } from '@shared/utils';

interface UseRequestTabsProps {
    requests: Request[];
    activeDate?: Date;
}

interface UseRequestTabsReturn {
    draftRequests: Request[];
    currentRequests: Request[];
    pastRequests: Request[];
    hasDrafts: boolean;
    hasCurrent: boolean;
    hasPast: boolean;
    totalCount: number;
}

/**
 * Filters requests into Drafts, Current, and Past tabs
 * - Drafts: Requests with DRAFT status AND (no deadline or deadline >= today)
 * - Current: Non-draft requests with endDate >= today AND (no deadline or deadline >= today)
 * - Past: Requests with deadline < today OR (non-draft AND endDate < today)
 */
export function useRequestTabs({
    requests,
    activeDate = new Date(),
}: UseRequestTabsProps): UseRequestTabsReturn {
    const normalizedActiveDate = normalizeToStartOfDay(activeDate);

    const { draftRequests, currentRequests, pastRequests } = useMemo((): {
        draftRequests: Request[];
        currentRequests: Request[];
        pastRequests: Request[];
    } => {
        const drafts: Request[] = [];
        const current: Request[] = [];
        const past: Request[] = [];

        requests.forEach((request): void => {
            const requestEnd = request.endDate
                ? normalizeToStartOfDay(request.endDate)
                : normalizedActiveDate;
            const hasPassedDeadline = request.deadline
                ? normalizeToStartOfDay(request.deadline) < normalizedActiveDate
                : false;

            // If deadline has passed, always go to Past
            if (hasPassedDeadline) {
                past.push(request);
                return;
            }

            // If no deadline passed, check status and end date
            if (request.status === RequestStatus.DRAFT) {
                drafts.push(request);
            } else {
                // Non-draft requests: check end date
                if (requestEnd >= normalizedActiveDate) {
                    current.push(request);
                } else {
                    past.push(request);
                }
            }
        });

        return {
            draftRequests: drafts,
            currentRequests: current,
            pastRequests: past,
        };
    }, [requests, normalizedActiveDate]);

    const hasDrafts = draftRequests.length > 0;
    const hasCurrent = currentRequests.length > 0;
    const hasPast = pastRequests.length > 0;
    const totalCount = requests.length;

    return {
        draftRequests,
        currentRequests,
        pastRequests,
        hasDrafts,
        hasCurrent,
        hasPast,
        totalCount,
    };
}
